import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const DataTable = ({ 
  columns = [], 
  data = [], 
  pageSize = 25, 
  loading = false,
  showSearch = true,
  showPagination = true,
  bordered = true,
  size = 'small',
  onRowClick = null,
  rowSelection = null,
  ...props 
}) => {
  const [searchText, setSearchText] = useState('');

  // Filter data based on search text
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    
    const searchLower = searchText.toLowerCase();
    return data.filter(record => 
      Object.values(record).some(value => 
        value && value.toString().toLowerCase().includes(searchLower)
      )
    );
  }, [data, searchText]);

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Enhanced columns with search functionality
  const enhancedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      // Only override sorter if not explicitly defined in the column
      ...(col.sorter === undefined && {
        sorter: (a, b) => {
          const aVal = a[col.dataIndex] || '';
          const bVal = b[col.dataIndex] || '';
          
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return aVal - bVal;
          }
          
          return aVal.toString().localeCompare(bVal.toString());
        }
      }),
      ...(col.searchable !== false && {
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 12 }}>
            <Input
              placeholder={`Search ${col.title}`}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm()}
              style={{ 
                marginBottom: 12, 
                display: 'block',
                borderRadius: 8,
                height: 36,
                padding: '8px 12px'
              }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                icon={<SearchOutlined />}
                size="small"
                style={{ 
                  width: 90,
                  borderRadius: 8,
                  height: 32,
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                  border: 'none',
                  boxShadow: '0 2px 6px rgba(24, 144, 255, 0.3)'
                }}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  clearFilters();
                  confirm();
                }}
                size="small"
                style={{ 
                  width: 90,
                  borderRadius: 8,
                  height: 32,
                  fontWeight: 500,
                  border: '1px solid #d9d9d9',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
        filterIcon: filtered => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
          const recordValue = record[col.dataIndex];
          return recordValue ? recordValue.toString().toLowerCase().includes(value.toLowerCase()) : false;
        }
      })
    }));
  }, [columns]);

  return (
    <div className="data-table-container">
      {showSearch && (
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search all columns..."
            allowClear
            prefix={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ 
              maxWidth: 400,
              borderRadius: 12,
              height: 48,
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #d9d9d9',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
            onPressEnter={(e) => handleSearch(e.target.value)}
          />
        </div>
      )}
      
      <Table
        columns={enhancedColumns}
        dataSource={filteredData}
        pagination={showPagination ? {
          pageSize: pageSize,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
        } : false}
        loading={loading}
        bordered={bordered}
        size={size}
        scroll={{ x: 'max-content' }}
        rowKey={(record, index) => record.id || record.key || index}
        onRow={onRowClick ? (record, index) => ({
          onClick: () => onRowClick(record, index),
          style: { cursor: 'pointer' }
        }) : undefined}
        rowSelection={rowSelection}
        {...props}
      />
      
      {filteredData.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          {searchText ? 'No records found matching your search' : 'No data available'}
        </div>
      )}
    </div>
  );
};

export default DataTable;
